import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCookie } from '@/utils/cookies'
import { COOKIE_NAMES } from '@/utils/cookies'
import { createFetchOptions } from '@/services/api/config'

const API_URL = import.meta.env.VITE_API_URL

interface ShareLink {
  id: number
  share_token: string
  shareUrl: string
  created_by: string
  expires_at: string | null
  max_views: number | null
  view_count: number
  created_at: string
  last_accessed_at: string | null
  isExpired: boolean
  isExhausted: boolean
  isActive: boolean
}

interface CreateShareLinkParams {
  modelId: string
  expiresIn?: '1d' | '7d' | '30d' | 'never'
  maxViews?: number
}

// Fetch share links for a model
export function useShareLinks(modelId: string) {
  return useQuery<ShareLink[]>({
    queryKey: ['shareLinks', modelId],
    queryFn: async () => {
      const token = getCookie(COOKIE_NAMES.AUTH_TOKEN)
      if (!token) throw new Error('No auth token')

      const response = await fetch(
        `${API_URL}/api/v2/admin/models/${modelId}/shares`,
        createFetchOptions({
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
      )

      if (!response.ok) {
        throw new Error('Failed to fetch share links')
      }

      const data = await response.json()
      return data.data
    },
    enabled: !!modelId,
  })
}

// Create a new share link
export function useCreateShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, expiresIn, maxViews }: CreateShareLinkParams) => {
      const token = getCookie(COOKIE_NAMES.AUTH_TOKEN)
      if (!token) throw new Error('No auth token')

      const response = await fetch(
        `${API_URL}/api/v2/admin/models/${modelId}/share`,
        createFetchOptions({
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ expiresIn, maxViews }),
        })
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create share link')
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shareLinks', variables.modelId] })
    },
  })
}

// Delete (revoke) a share link
export function useDeleteShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, shareId }: { modelId: string; shareId: number }) => {
      const token = getCookie(COOKIE_NAMES.AUTH_TOKEN)
      if (!token) throw new Error('No auth token')

      const response = await fetch(
        `${API_URL}/api/v2/admin/models/${modelId}/share/${shareId}`,
        createFetchOptions({
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
      )

      if (!response.ok) {
        throw new Error('Failed to delete share link')
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shareLinks', variables.modelId] })
    },
  })
}

// Get model by share token (for public viewer)
export function useSharedModel(shareToken: string) {
  return useQuery({
    queryKey: ['sharedModel', shareToken],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v2/share/${shareToken}`)

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch shared model')
      }

      const data = await response.json()
      return data.data
    },
    enabled: !!shareToken,
  })
}